import { UniversalLayout as ZDSUniversalLayout } from '@zapier/universal-layout';
import { ReactNode, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { CurrentAccountIdProvider } from '@zapier/identity';
import {
  EventAction,
  InteractionName,
  useAvroTracking,
  useTrackEvent,
} from '../../observability';
import {
  Alert,
  AlertDescription,
  AlertType,
  Colors,
} from '@zapier/design-system';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { COLOR_KEYS } from '../../constants';
import { AlertBanner } from '../../builder';

type Props = {
  alertBanner?: AlertBanner;
  children: ReactNode;
  hideFooter?: boolean;
  hideHeader?: boolean;
  shouldRenderAlphabetNav?: boolean;
  shouldRenderSeoLinks?: boolean;
};

const alertBannerBackgroundColorMap: Record<AlertType, keyof typeof Colors> = {
  error: COLOR_KEYS.error400,
  info: COLOR_KEYS.blue,
  success: COLOR_KEYS.success400,
  warning: COLOR_KEYS.warning400,
};

export const UniversalLayout = ({
  children,
  hideFooter = false,
  hideHeader = false,
  shouldRenderAlphabetNav = false,
  shouldRenderSeoLinks = false,
  alertBanner,
}: Props) => {
  const [showAlertBanner, setShowAlertBanner] = useState(true);
  const richTextRef = useRef<HTMLDivElement>(null);
  const { emitUserInteractionEvent } = useAvroTracking();
  const trackEvent = useTrackEvent();
  const currentUrl = useRouter()?.asPath || '';

  const handleDismissAlertBanner = () => {
    setShowAlertBanner(false);
    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.dismiss,
      option_selected: 'dismiss_button',
      custom_tracking_fields: { component: 'UniversalLayout' },
      interaction_raw_text:
        (alertBanner?.layout === 'mini'
          ? richTextRef.current?.innerText.trim()
          : alertBanner?.title) || '',
    });
  };

  return (
    <CurrentAccountIdProvider>
      <ZDSUniversalLayout
        currentUrl={currentUrl}
        renderAlertBanner={() =>
          showAlertBanner &&
          (alertBanner?.layout === 'mini'
            ? !!alertBanner?.body
            : !!alertBanner?.title && !!alertBanner?.type) ? (
            <Alert
              layout={alertBanner?.layout}
              miniBackgroundColor={alertBanner?.miniBackgroundColor}
              title={alertBanner?.title}
              type={alertBanner?.type}
              {...(alertBanner?.dismissable && {
                onDismiss: handleDismissAlertBanner,
              })}
            >
              <AlertDescription>
                {alertBanner?.body && (
                  <RichTextHtml
                    backgroundColor={
                      alertBanner.layout === 'mini'
                        ? alertBanner.miniBackgroundColor
                        : alertBanner.type &&
                          alertBannerBackgroundColorMap[alertBanner.type]
                    }
                    html={alertBanner.body}
                    ref={richTextRef}
                  />
                )}
              </AlertDescription>
            </Alert>
          ) : null
        }
        renderFooter={hideFooter ? () => null : undefined}
        renderHeader={hideHeader ? () => null : undefined}
        shouldRenderAlphabetNav={shouldRenderAlphabetNav}
        shouldRenderSeoLinks={shouldRenderSeoLinks}
        // @ts-ignore: as suggested by Alej (https://github.com/zapier/blog/pull/375#discussion_r545454080)
        trackEvent={trackEvent}
      >
        {children}
      </ZDSUniversalLayout>
    </CurrentAccountIdProvider>
  );
};
